import React, { useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { post } from '../utils/api';
import { useBattleGame } from '../hooks/useBattleGame';
import { RushTopBar } from '../components/trivia-rush/RushTopBar';
import { RushWavyGrid } from '../components/trivia-rush/RushWavyGrid';
import { BattleLobby } from '../components/trivia-rush/battle/BattleLobby';
import { BattleGame } from '../components/trivia-rush/battle/BattleGame';
import { BattleResults } from '../components/trivia-rush/battle/BattleResults';
import { UserModal } from '../components/ui/modals/UserModal';
import { OnboardingSurveyGate } from '../components/OnboardingSurveyGate';

const BattleRushPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showUserModal, setShowUserModal] = useState(false);
  const {
    state,
    isHost,
    myAnswer,
    startGame,
    submitAnswer,
    leaveRoom,
    toggleReady,
    restartRoom,
  } = useBattleGame(code);

  const handleJoinRoom = useCallback((newCode: string) => {
    // Fire-and-forget leave current room
    if (code) {
      post(`/api/trivia-rush/battle/${code}/leave`, {}).catch(() => {});
    }
    navigate(`/trivia-rush/battle/${newCode}`);
  }, [code, navigate]);

  // Not signed in
  if (!user) {
    return (
      <div
        className="min-h-screen flex flex-col relative rush-theme"
        style={{ backgroundColor: 'var(--rush-bg-primary)' }}
      >
        <RushTopBar />
        <RushWavyGrid />
        <div className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center relative z-[1]">
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight mb-3"
            style={{ color: 'var(--rush-text-primary)', fontFamily: 'DM Serif Display, serif' }}
          >
            BATTLE MODE
          </h1>
          <p className="text-base mb-8" style={{ color: 'var(--rush-text-muted)' }}>
            Sign in to join this battle
          </p>
          <button
            onClick={() => setShowUserModal(true)}
            className="px-8 py-3 rounded-full text-base font-bold transition-all hover:scale-105 mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--rush-purple-mid), #7C3AED)',
              color: 'white',
            }}
          >
            Sign In
          </button>
          <Link
            to="/trivia-rush"
            className="text-sm no-underline transition-opacity hover:opacity-80"
            style={{ color: 'var(--rush-text-muted)' }}
          >
            Go to Trivia Rush
          </Link>
        </div>
        <UserModal
          isOpen={showUserModal}
          onClose={() => setShowUserModal(false)}
          className="rush-theme"
        />
      </div>
    );
  }

  // Loading
  if (state.isLoading && !state.room) {
    return (
      <div
        className="min-h-screen flex flex-col relative rush-theme"
        style={{ backgroundColor: 'var(--rush-bg-primary)' }}
      >
        <RushTopBar />
        <RushWavyGrid />
        <div className="flex items-center justify-center min-h-[80vh] relative z-[1]">
          <p className="text-lg animate-pulse" style={{ color: 'var(--rush-text-muted)' }}>
            Joining room...
          </p>
        </div>
      </div>
    );
  }

  // Error (no room loaded)
  if (state.error && !state.room) {
    return (
      <div
        className="min-h-screen flex flex-col relative rush-theme"
        style={{ backgroundColor: 'var(--rush-bg-primary)' }}
      >
        <RushTopBar />
        <RushWavyGrid />
        <div className="flex flex-col items-center justify-center min-h-[80vh] px-6 text-center relative z-[1]">
          <p className="text-lg mb-4" style={{ color: 'var(--rush-wrong)' }}>
            {state.error}
          </p>
          <Link
            to="/trivia-rush"
            className="px-8 py-3 rounded-full text-base font-bold no-underline transition-all hover:scale-105"
            style={{
              backgroundColor: 'var(--rush-purple-mid)',
              color: 'var(--rush-text-primary)',
            }}
          >
            Back to Trivia Rush
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col relative rush-theme"
      style={{ backgroundColor: 'var(--rush-bg-primary)' }}
    >
      <RushTopBar />
      <RushWavyGrid />

      <div className="relative z-[1] flex-1">
        {state.phase === 'lobby' && state.room && (
          <BattleLobby
            room={state.room}
            currentUserId={user.id}
            isHost={isHost}
            onStart={startGame}
            onLeave={leaveRoom}
            onToggleReady={toggleReady}
            onJoinRoom={handleJoinRoom}
            error={state.error}
          />
        )}

        {state.phase === 'playing' && state.room && (
          <BattleGame
            state={state}
            currentUserId={user.id}
            myAnswer={myAnswer}
            onAnswer={submitAnswer}
          />
        )}

        {state.phase === 'results' && state.room && (
          <BattleResults
            room={state.room}
            currentUserId={user.id}
            isHost={isHost}
            onRestart={restartRoom}
          />
        )}
      </div>
      <OnboardingSurveyGate />
    </div>
  );
};

export default BattleRushPage;
